// ========================================
// EQUIPMENT RESOLVER UTILITY
// ========================================
// [CREATED: NOV 23, 2025]
// [PURPOSE]: Resolve D&D equipment strings to BASE_ITEMS database entries
// [WHY]: Equipment strings use display names ("Leather armor") but BASE_ITEMS uses keys ("leather")
// [ROOT CAUSE]: CharacterCreationWizard and App.jsx were creating 1x1 fallback items instead of using database

import { BASE_ITEMS, STARTER_PACKS } from '../data/gameData';
import { ARMOR_WEIGHTS } from '../constants/enums';
import { BASE_ITEM_CATEGORY_IDS } from '../constants/baseItemCategories';

/**
 * [FIX APR 2026 sub-project G]: Armor-weight proficiency string map.
 * The character sheet stores armor proficiencies as human display strings
 * ("Light armor", "Medium armor", "Heavy armor", "Shields", "All armor").
 * This maps the canonical ARMOR_WEIGHTS enum values → the proficiency string
 * we need to find in the character's list. Single source for both the enum
 * value and the matching display string.
 */
const ARMOR_PROFICIENCY_LABEL = {
  [ARMOR_WEIGHTS.LIGHT]:  'Light armor',
  [ARMOR_WEIGHTS.MEDIUM]: 'Medium armor',
  [ARMOR_WEIGHTS.HEAVY]:  'Heavy armor',
  [ARMOR_WEIGHTS.SHIELD]: 'Shields',
};

/**
 * Equipment Resolver Class
 * Resolves human-readable equipment strings to actual BASE_ITEMS database entries
 *
 * Examples:
 * - "Leather armor" → BASE_ITEMS.armor.leather
 * - "4 javelins" → BASE_ITEMS.weapons.javelin × 4
 * - "Light crossbow and 20 bolts" → [crossbow_light, crossbow_bolts]
 * - "Any martial weapon" → BASE_ITEMS.weapons.longsword (default)
 */
export class EquipmentResolver {

  /**
   * Main resolution function
   * @param {string|object} equipment - Equipment string or choice object from SRD
   * @param {object} character - Character data for proficiency checks
   * @returns {Array} Array of resolved item objects from BASE_ITEMS
   */
  static resolve(equipment, character = {}) {
    // Handle choice objects: { choice: ['Option 1', 'Option 2'] }
    if (typeof equipment === 'object' && equipment.choice) {
      // For choices, we'll return metadata that UI can use for selection
      // For now, default to first choice
      return this.resolveString(equipment.choice[0], character);
    }

    // Handle string equipment
    if (typeof equipment === 'string') {
      return this.resolveString(equipment, character);
    }

    console.warn('[EquipmentResolver] Unknown equipment type:', equipment);
    return [];
  }

  /**
   * Resolve single equipment string to BASE_ITEMS entries
   * @param {string} equipmentString - Human-readable equipment string
   * @param {object} character - Character data
   * @returns {Array} Array of item objects
   */
  static resolveString(equipmentString, character = {}) {
    const trimmed = equipmentString.trim();

    // Remove parentheses first (they group items but don't affect parsing)
    const cleaned = trimmed.replace(/^\(|\)$/g, '');

    // Priority 1: Check if it's a starter pack
    const packMatch = this.findStarterPack(cleaned);
    if (packMatch) {
      return [{ ...packMatch, source: 'STARTER_PACKS', originalString: trimmed }];
    }

    // Priority 2: Check for combo items ("X and Y")
    if (cleaned.includes(' and ')) {
      return this.parseCombo(cleaned, character);
    }

    // Priority 3: Check for conditional equipment "(if proficient)"
    const conditional = this.parseConditional(cleaned, character);
    if (conditional) {
      if (!conditional.allowed) {
        console.info(`[EquipmentResolver] Conditional item not allowed: "${cleaned}" - ${conditional.reason}`);
        return []; // Skip items character isn't proficient with
      }
      // Re-resolve without the conditional
      return this.resolveString(conditional.item, character);
    }

    // [FIX NOV 28, 2025]: Priority 3.5 - Handle "quiver of X arrows" pattern specially
    // [WHY]: Should return BOTH the quiver container AND the arrows, not just arrows
    // [ROOT CAUSE]: Previous alias just mapped to 'arrow', losing the quiver container
    const quiverMatch = cleaned.toLowerCase().match(/^quiver\s+(?:of\s+)?(\d+)?\s*(arrow|bolt)s?$/i);
    if (quiverMatch) {
      const arrowQty = parseInt(quiverMatch[1]) || 20; // Default 20 if no number
      const ammoType = quiverMatch[2].toLowerCase() === 'bolt' ? 'crossbow_bolts' : 'arrow';

      // Get quiver from containers
      const quiver = BASE_ITEMS.containers?.quiver;
      const ammo = BASE_ITEMS.ammunition?.[ammoType];

      const results = [];
      if (quiver) {
        results.push({
          ...quiver,
          category: 'containers',
          baseType: 'quiver',
          name: 'Quiver',
          source: 'BASE_ITEMS',
          originalString: trimmed
        });
      }
      if (ammo) {
        results.push({
          ...ammo,
          category: 'ammunition',
          baseType: ammoType,
          name: ammoType === 'arrow' ? `Arrows (${arrowQty})` : `Crossbow Bolts (${arrowQty})`,
          quantity: arrowQty,
          bundledQuantity: arrowQty,
          source: 'BASE_ITEMS',
          originalString: trimmed
        });
      }
      if (results.length > 0) return results;
    }

    // Priority 4: Parse quantity ("4 javelins", "Two daggers")
    const { quantity, item: itemName } = this.parseQuantity(cleaned);

    // Priority 5: Handle generic choices ("Any martial weapon")
    if (itemName.toLowerCase().startsWith('any ')) {
      const resolved = this.resolveGenericChoice(itemName, character);
      if (resolved) {
        // Return quantity copies
        return Array(quantity).fill(null).map(() => ({ ...resolved }));
      }
    }

    // Priority 6: Try direct lookup in BASE_ITEMS
    const normalized = this.normalizeItemName(itemName);
    const directMatch = this.findInBaseItems(normalized);
    if (directMatch) {
      // [FIX NOV 26, 2025]: Handle ammunition specially - don't multiply bundles
      // [WHY]: "20 bolts" should return 1 bundle item (which already contains 20 bolts),
      //        not 20 separate bundle items (which would be 400 bolts!)
      // [D&D 5e PHB]: Ammunition is sold in bundles of 20 (arrows, bolts, bullets)
      if (directMatch.category === 'ammunition') {
        // [BUG FIX MAY 2026 / #208]: Ammunition is bundled but the bundle
        //   COUNT (e.g., "20 bolts" → 20) was being dropped here. Pre-fix,
        //   the InventoryItem constructor would fall through to default
        //   quantity=1, so a Cleric's "Light crossbow and 20 bolts" SRD
        //   starting equipment delivered 1 bolt instead of 20. Same for
        //   arrows / sling bullets / blowgun needles — every ammo bundle
        //   in the SRD class equipment list. Now we pass `quantity` from
        //   the upstream parseQuantity step so the constructor reads it.
        return [{
          ...directMatch,
          quantity,
          bundledQuantity: quantity,
          source: 'BASE_ITEMS',
          originalString: trimmed
        }];
      }

      // [PASS-D APR 30 2026]: Removed thrown-weapon bundling block (was here
      // since Nov 27 2025). Per user binding answer #1: "'Two daggers'
      // should produce 2 separate grid items, not 1 item with quantity:2.
      // Same for 'Three javelins', '10 darts', etc. — all thrown weapons
      // separate." Ammunition (arrows/bolts/sling bullets) STILL bundles
      // above (RAW says 20-per-bundle); thrown weapons fall through to
      // the per-item copy loop below.
      //
      // The user-facing stack/un-stack control now lives in
      // EditItemDialog (Pass-D Stackable toggle); a Rogue who wants to
      // bundle 6 throwing daggers can flip it on per-instance. Default
      // BASE_ITEMS data has dagger/javelin/etc. as `stackable: false`.

      // Normal items: create quantity copies
      const items = Array(quantity).fill(null).map(() => ({
        ...directMatch,
        source: 'BASE_ITEMS',
        originalString: trimmed
      }));
      return items;
    }

    // Priority 7: Handle currency parsing ("Belt pouch containing 15 gp")
    const currencyParsed = this.parseCurrency(cleaned);
    if (currencyParsed) {
      return [currencyParsed];
    }

    // Priority 8: FALLBACK - Item not found in database
    // This should rarely happen now that we've added missing items
    console.warn(`[EquipmentResolver] Item not found in BASE_ITEMS: "${trimmed}"`);
    console.warn(`[EquipmentResolver] Normalized to: "${normalized}"`);
    console.warn(`[EquipmentResolver] Creating fallback placeholder item`);

    return [{
      name: cleaned,
      category: 'items',
      baseType: 'misc',
      source: 'FALLBACK',
      originalString: trimmed,
      weight: 1,
      size: [1, 1],
      baseValue: 0,
      icon: '📦',
      description: `Fallback item created from: "${trimmed}"`
    }];
  }

  /**
   * Convert database key to proper display name
   * Handles: underscores → spaces, title case, special patterns
   *
   * Examples:
   * - "mess_kit" → "Mess Kit"
   * - "longsword" → "Longsword"
   * - "crossbow_light" → "Light Crossbow"
   * - "rope_hempen_50ft" → "Hempen Rope (50 ft.)"
   * - "chain_mail" → "Chain Mail"
   *
   * @param {string} baseType - Item base type key
   * @param {number} bundledQuantity - Optional quantity for bundled items (darts, javelins)
   */
  static formatDisplayName(baseType, bundledQuantity = null) {
    if (!baseType) return 'Unknown Item';

    // Special case mappings for items that need custom formatting
    const specialNames = {
      'crossbow_light': 'Light Crossbow',
      'crossbow_heavy': 'Heavy Crossbow',
      'crossbow_hand': 'Hand Crossbow',
      'crossbow_bolts': 'Crossbow Bolts',
      'crossbow_bolt': 'Crossbow Bolts',  // [FIX NOV 25, 2025]: Singular alias
      'bolt': 'Crossbow Bolts',           // [FIX NOV 25, 2025]: Short alias
      'light_hammer': 'Light Hammer',
      'rope_hempen_50ft': 'Hempen Rope (50 ft.)',
      'rope_silk_50ft': 'Silk Rope (50 ft.)',
      'pick_miners': "Miner's Pick",
      'oil_flask': 'Flask of Oil',
      'holy_symbol': 'Holy Symbol',
      'arcane_focus': 'Arcane Focus',
      'druidic_focus': 'Druidic Focus',
      'thieves_tools': "Thieves' Tools",
      'smith_tools': "Smith's Tools",
      'brewers_supplies': "Brewer's Supplies",
      'alchemists_supplies': "Alchemist's Supplies",
      'clothes_common': 'Common Clothes',
      'clothes_fine': 'Fine Clothes',
      'clothes_traveler': "Traveler's Clothes",
      'clothes_costume': 'Costume Clothes',
      'belt_pouch': 'Belt Pouch',
      'rations': 'Rations (1 day)',
      'mess_kit': 'Mess Kit',
      'waterskin': 'Waterskin',
      'tinderbox': 'Tinderbox',
      'torch': 'Torch',
      'bedroll': 'Bedroll',
      'backpack': 'Backpack',
      'component_pouch': 'Component Pouch',
      // [FIX NOV 25, 2025]: Added new special names for character creation
      'arrow': 'Arrows (20)',
      'arrows': 'Arrows (20)',
      'spellbook': 'Spellbook',
      'wooden_shield': 'Wooden Shield',
      'sling_bullet': 'Sling Bullets (20)',
      'sling_bullets': 'Sling Bullets (20)',

      // [FIX NOV 27, 2025]: Magic weapon names - proper D&D formatting
      // [WHY]: "longsword_plus_1" should display as "+1 Longsword", not "Longsword Plus 1"
      'longsword_plus_1': '+1 Longsword',
      'longsword_plus_2': '+2 Longsword',
      'longsword_plus_3': '+3 Longsword',
      'shortsword_plus_1': '+1 Shortsword',
      'shortsword_plus_2': '+2 Shortsword',
      'greatsword_plus_1': '+1 Greatsword',
      'greatsword_plus_2': '+2 Greatsword',
      'dagger_plus_1': '+1 Dagger',
      'dagger_plus_2': '+2 Dagger',
      'longbow_plus_1': '+1 Longbow',
      'longbow_plus_2': '+2 Longbow',
      'shortbow_plus_1': '+1 Shortbow',
      'rapier_plus_1': '+1 Rapier',
      'rapier_plus_2': '+2 Rapier',
      'battleaxe_plus_1': '+1 Battleaxe',
      'battleaxe_plus_2': '+2 Battleaxe',
      'warhammer_plus_1': '+1 Warhammer',
      'warhammer_plus_2': '+2 Warhammer',
      'mace_plus_1': '+1 Mace',
      'mace_plus_2': '+2 Mace',

      // Named magic weapons
      'flame_tongue': 'Flame Tongue',
      'frost_brand': 'Frost Brand',
      'vicious_weapon': 'Vicious Weapon',
      'silvered_longsword': 'Silvered Longsword',

      // Magic items - wands, rings, etc.
      'wand_magic_missiles': 'Wand of Magic Missiles',
      'wand_cure_wounds': 'Wand of Cure Wounds',
      'ring_protection': 'Ring of Protection',
      'cloak_elvenkind': 'Cloak of Elvenkind',
      'staff_healing': 'Staff of Healing',
      'potion_climbing': 'Potion of Climbing',
      'cloak_billowing': 'Cloak of Billowing',
      'dread_helm': 'Dread Helm',
      'bag_of_holding': 'Bag of Holding',
      'boots_of_speed': 'Boots of Speed',
      'gauntlets_ogre_power': 'Gauntlets of Ogre Power',
      'headband_intellect': 'Headband of Intellect',
      'amulet_health': 'Amulet of Health',
      'belt_giant_strength': 'Belt of Giant Strength',
      'cloak_displacement': 'Cloak of Displacement',
      'ring_spell_storing': 'Ring of Spell Storing',
      'staff_power': 'Staff of Power',
      'rod_pact_keeper': 'Rod of the Pact Keeper',

      // [FIX JAN 13, 2026]: Base armor display names - "leather" → "Leather Armor"
      // [ROOT CAUSE]: Druid starting equipment showed "Leather" not "Leather Armor"
      'leather': 'Leather Armor',
      'padded': 'Padded Armor',
      'studded_leather': 'Studded Leather Armor',
      'hide': 'Hide Armor',
      'chain_shirt': 'Chain Shirt',
      'scale_mail': 'Scale Mail',
      'breastplate': 'Breastplate',
      'half_plate': 'Half Plate Armor',
      'ring_mail': 'Ring Mail',
      'chain_mail': 'Chain Mail',
      'splint': 'Splint Armor',
      'plate': 'Plate Armor',
      'shield': 'Shield',

      // Armor variants (+1, +2, etc.)
      'plate_plus_1': '+1 Plate Armor',
      'plate_plus_2': '+2 Plate Armor',
      'chain_mail_plus_1': '+1 Chain Mail',
      'leather_plus_1': '+1 Leather Armor',
      'studded_leather_plus_1': '+1 Studded Leather Armor',
      'shield_plus_1': '+1 Shield',
      'shield_plus_2': '+2 Shield'
    };

    // Check special mappings first
    let baseName;
    if (specialNames[baseType]) {
      baseName = specialNames[baseType];
    } else {
      // General conversion: underscores to spaces, title case
      // [NOTE]: Same pattern as ItemDialog.js line 391
      baseName = baseType
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    }

    // [FIX NOV 27, 2025]: Append quantity for bundled thrown weapons
    // [WHY]: "10 darts" should display as "Darts (10)" not just "Dart"
    if (bundledQuantity && bundledQuantity > 1) {
      // Pluralize if needed (simple heuristic)
      let pluralName = baseName;
      if (!baseName.endsWith('s') && !baseName.includes('(')) {
        pluralName = baseName + 's';
      }
      return `${pluralName} (${bundledQuantity})`;
    }

    return baseName;
  }

  /**
   * Normalize item name for database lookup
   * Handles: capitalization, spaces, underscores, plurals, suffixes
   *
   * Examples:
   * - "Leather Armor" → "leather"
   * - "Explorer's Pack" → "explorer"
   * - "Two Handaxes" → "handaxe"
   * - "Scale Mail" → "scale_mail"
   */
  static normalizeItemName(name) {
    let normalized = name
      .toLowerCase()
      .trim()
      .replace(/[']/g, '');           // Remove apostrophes: "Explorer's" → "Explorers"

    // [FIX NOV 25, 2025]: Remove common PREFIXES first
    // "Set of traveler's clothes" → "traveler's clothes"
    // "A leather armor" → "leather armor"
    normalized = normalized
      .replace(/^(a|an|set of|pair of)\s+/i, '');

    // Remove common suffixes
    normalized = normalized
      .replace(/\s+(armor|mail)$/i, '')     // "Leather armor" → "leather"
      .replace(/\s+pack$/i, '')             // "Explorer pack" → "explorer"
      .replace(/\s+focus$/i, '_focus');     // "Arcane focus" → "arcane_focus"

    // Handle multi-word items
    normalized = normalized
      .replace(/\s+/g, '_');                // Spaces → underscores

    // Handle plurals (careful! only at the end)
    // "Handaxes" → "handaxe", but not "Glass" → "Glas"
    if (normalized.endsWith('ves')) {
      normalized = normalized.slice(0, -3) + 'f';  // "Halves" → "half"
    } else if (normalized.endsWith('ies')) {
      normalized = normalized.slice(0, -3) + 'y';  // "Berries" → "berry"
    } else if (normalized.endsWith('es') && !normalized.endsWith('shes') && !normalized.endsWith('ches')) {
      // "Boxes" → "box", but not "Bushes" → "bush"
      if (normalized.endsWith('xes') || normalized.endsWith('zes') || normalized.endsWith('ses')) {
        normalized = normalized.slice(0, -2);
      }
    } else if (normalized.endsWith('s') && normalized.length > 3) {
      // Remove trailing 's' for plurals: "javelins" → "javelin"
      // But NOT for items that naturally end in 's': "glass", "compass"
      const endsInDoubleS = normalized.endsWith('ss');
      const endsInUs = normalized.endsWith('us');
      if (!endsInDoubleS && !endsInUs) {
        normalized = normalized.slice(0, -1);
      }
    }

    return normalized;
  }

  /**
   * Find item in BASE_ITEMS by searching all categories
   * @param {string} normalizedKey - Normalized item key to search for
   * @returns {object|null} Item data with category and key, or null
   */
  static findInBaseItems(normalizedKey) {
    // [FIX NOV 25, 2025]: Alias mapping for items where normalized names don't match BASE_ITEMS keys
    // Problem: "travelers clothes" normalizes to "travelers_clothe" but key is "clothes_travelers"
    // [FIX JAN 12, 2026]: Added armor aliases to ensure resolution works for all starting equipment
    const aliases = {
      // [PASS-D APR 30 2026]: 'handax' alias added to fix a long-standing
      //   pluralization bug in normalizeItemName: "handaxes" hits the
      //   "xes → strip 2" rule and becomes "handax" instead of "handaxe".
      //   "Two handaxes" appears in Barbarian and Fighter SRD starting
      //   equipment, so this was silently fallback-resolving for both
      //   classes since the pluralization rule was added. Found while
      //   removing the thrown-weapon bundling block — bundle code masked
      //   the failure by triggering on a DIFFERENT input shape (numeric
      //   prefix + plural). Discovery via Pass-D test
      //   `equipmentResolver.thrownWeapons.test.js`.
      'handax': 'handaxe',
      // [PASS-D APR 30 2026]: Crossbow aliases — BASE_ITEMS keys are
      //   "crossbow_light" / "crossbow_heavy" / "crossbow_hand", but
      //   normalizeItemName produces "light_crossbow" / "heavy_crossbow"
      //   / "hand_crossbow" from the human-readable equipment strings
      //   ("Light crossbow", etc.). Aliasing both directions keeps the
      //   data layer untouched. Found via the same Pass-D test sweep.
      'light_crossbow': 'crossbow_light',
      'heavy_crossbow': 'crossbow_heavy',
      'hand_crossbow': 'crossbow_hand',
      // Throwing dagger normalizes cleanly ("Throwing dagger" → "throwing_dagger")
      // and the BASE_ITEMS key matches; no alias needed.
      'travelers_clothe': 'clothes_travelers',
      'travelers_clothes': 'clothes_travelers',
      'traveler_clothe': 'clothes_travelers',
      'traveler_clothes': 'clothes_travelers',
      'common_clothe': 'clothes_common',
      'common_clothes': 'clothes_common',
      'fine_clothe': 'clothes_fine',
      'fine_clothes': 'clothes_fine',
      'costume_clothe': 'clothes_costume',
      'costume_clothes': 'clothes_costume',
      'hooded_lantern': 'lantern_hooded',
      'bullseye_lantern': 'lantern_bullseye',
      'hempen_rope': 'rope_hempen_50ft',
      'silk_rope': 'rope_silk_50ft',
      'holy_symbol': 'holy_symbol_amulet',
      'druidic_focus': 'druidic_focus_mistletoe',
      'arcane_focus': 'arcane_focus_crystal',
      'belt_pouch': 'pouch',
      'spell_component_pouch': 'component_pouch',
      // [FIX JAN 12, 2026]: Armor aliases - normalizeItemName strips " armor" suffix
      // but might produce variations depending on input format
      'leather_armor': 'leather',
      'studded_leather': 'studded_leather',
      'chain_mail': 'chain_mail',
      'scale_mail': 'scale_mail',
      'ring_mail': 'ring_mail',
      'half_plate': 'half_plate',
      'plate_armor': 'plate',
      'padded_armor': 'padded',
      'hide_armor': 'hide'
      // [REMOVED NOV 28, 2025]: Quiver aliases removed - now handled by quiverMatch pattern
      // [WHY]: New pattern returns BOTH quiver container AND arrows instead of just arrows
    };

    // Try alias first
    const actualKey = aliases[normalizedKey] || normalizedKey;

    // [PHASE B / #019 — B2-5]: Categories to search are sourced from the
    // BASE_ITEM_CATEGORIES data block (single source of truth). Pre-fix
    // this hardcoded list had 'magic_items' which doesn't match the
    // gameData.js storage key 'magical_items', so items in that bucket
    // silently failed to resolve via findInBaseItems — fixed here as
    // a side effect of switching to the data-block ids.
    const categories = BASE_ITEM_CATEGORY_IDS;

    for (const category of categories) {
      if (BASE_ITEMS[category] && BASE_ITEMS[category][actualKey]) {
        return {
          ...BASE_ITEMS[category][actualKey],
          category,
          baseType: actualKey,
          name: this.formatDisplayName(actualKey)  // [FIX NOV 24, 2025]: Add proper display name
        };
      }
    }

    return null;
  }

  /**
   * Find starter pack by normalized name
   * @param {string} packString - Pack name string
   * @returns {object|null} Pack data or null
   */
  static findStarterPack(packString) {
    const normalized = this.normalizeItemName(packString);

    if (STARTER_PACKS && STARTER_PACKS[normalized]) {
      // [FIX NOV 24, 2025]: Add proper display name for starter packs
      const packDisplayNames = {
        'explorer': "Explorer's Pack",
        'dungeoneer': "Dungeoneer's Pack",
        'burglar': "Burglar's Pack",
        'diplomat': "Diplomat's Pack",
        'entertainer': "Entertainer's Pack",
        'priest': "Priest's Pack",
        'scholar': "Scholar's Pack"
      };

      return {
        ...STARTER_PACKS[normalized],
        baseType: normalized,
        name: packDisplayNames[normalized] || this.formatDisplayName(normalized + '_pack'),
        isPack: true
      };
    }

    return null;
  }

  /**
   * Parse quantity from equipment string
   * Handles: "4 javelins", "Two daggers", "Twenty arrows"
   *
   * @param {string} equipmentString - Equipment string with possible quantity
   * @returns {object} { quantity: number, item: string }
   */
  static parseQuantity(equipmentString) {
    // Try number prefix: "4 javelins", "20 arrows"
    const numberMatch = equipmentString.match(/^(\d+)\s+(.+)$/);
    if (numberMatch) {
      return {
        quantity: parseInt(numberMatch[1]),
        item: numberMatch[2]
      };
    }

    // Try word prefix: "Two daggers", "Five javelins"
    const wordMatch = equipmentString.match(/^(Two|Three|Four|Five|Six|Seven|Eight|Nine|Ten|Twenty|Thirty|Forty|Fifty)\s+(.+)$/i);
    if (wordMatch) {
      const wordToNumber = {
        two: 2, three: 3, four: 4, five: 5,
        six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
        twenty: 20, thirty: 30, forty: 40, fifty: 50
      };
      const word = wordMatch[1].toLowerCase();
      return {
        quantity: wordToNumber[word] || 1,
        item: wordMatch[2]
      };
    }

    // Try "of" quantity: "50 feet of silk rope"
    const ofMatch = equipmentString.match(/^(\d+)\s+\w+\s+of\s+(.+)$/);
    if (ofMatch) {
      // For "50 feet of silk rope", the rope itself is "rope_silk_50ft" (one item)
      // So we return quantity 1 but keep the full string for lookup
      return {
        quantity: 1,
        item: equipmentString  // Keep full string like "50 feet of silk rope"
      };
    }

    // No quantity prefix
    return {
      quantity: 1,
      item: equipmentString
    };
  }

  /**
   * Parse combo items ("X and Y")
   * Handles: "Light crossbow and 20 bolts", "Martial weapon and shield"
   *
   * @param {string} equipmentString - Combo equipment string
   * @param {object} character - Character data
   * @returns {Array} Array of resolved items
   */
  static parseCombo(equipmentString, character) {
    // Split on " and " (case insensitive)
    const parts = equipmentString.split(/\s+and\s+/i);

    const resolved = [];
    for (const part of parts) {
      const items = this.resolveString(part.trim(), character);
      if (items && items.length > 0) {
        resolved.push(...items);
      }
    }

    return resolved.length > 0 ? resolved : null;
  }

  /**
   * Parse conditional equipment ("Warhammer (if proficient)")
   * @param {string} equipmentString - Equipment string with possible condition
   * @param {object} character - Character data for proficiency check
   * @returns {object|null} { item: string, allowed: boolean, reason: string }
   */
  static parseConditional(equipmentString, character) {
    const conditionalMatch = equipmentString.match(/^(.+?)\s*\(if\s+(.+?)\)$/i);

    if (conditionalMatch) {
      // eslint-disable-next-line no-unused-vars
      const [_fullMatch, itemName, condition] = conditionalMatch;

      if (condition.toLowerCase() === 'proficient') {
        // Check if item exists first
        const normalized = this.normalizeItemName(itemName);
        const itemData = this.findInBaseItems(normalized);

        if (!itemData) {
          return {
            item: itemName,
            allowed: false,
            reason: 'Item not found in database'
          };
        }

        // Check proficiency
        const isProficient = this.checkProficiency(itemData, character);

        return {
          item: itemName,
          allowed: isProficient,
          reason: isProficient ? null : 'Not proficient with this item'
        };
      }
    }

    return null;
  }

  /**
   * Check if character is proficient with an item
   * @param {object} itemData - Item data from BASE_ITEMS
   * @param {object} character - Character data
   * @returns {boolean} True if proficient or no proficiency check needed
   */
  static checkProficiency(itemData, character) {
    // If no proficiency data, allow by default
    if (!character.proficiencies) return true;

    const profs = character.proficiencies;

    // Check weapon proficiency
    if (itemData.category === 'weapons') {
      if (!profs.weapons) return true; // No proficiency restrictions

      const weaponName = itemData.baseType.replace(/_/g, ' ');

      // Check specific weapon proficiency
      if (profs.weapons.some(w => w.toLowerCase().includes(weaponName))) {
        return true;
      }

      // Check category proficiency (Simple/Martial weapons)
      const simpleWeapons = [
        'club', 'dagger', 'greatclub', 'handaxe', 'javelin', 'light_hammer',
        'mace', 'quarterstaff', 'sickle', 'spear', 'crossbow_light', 'dart',
        'shortbow', 'sling'
      ];

      const isSimple = simpleWeapons.includes(itemData.baseType);

      if (isSimple && profs.weapons.includes('Simple weapons')) {
        return true;
      }

      if (!isSimple && profs.weapons.includes('Martial weapons')) {
        return true;
      }

      return false;
    }

    // Check armor proficiency
    if (itemData.category === 'armor') {
      if (!profs.armor) return true; // No armor proficiency restrictions

      // [FIX APR 2026 sub-project G]: Enum-driven armor proficiency check.
      // Data-driven replacement for the 4-way string-compare chain.
      const label = ARMOR_PROFICIENCY_LABEL[itemData.armorType];
      if (label && profs.armor.includes(label)) return true;

      // Check "All armor" — grants proficiency with every armor weight
      if (profs.armor.includes('All armor')) return true;

      return false;
    }

    // Non-weapon/armor items don't need proficiency
    return true;
  }

  /**
   * Resolve generic choice ("Any martial weapon", "Any simple weapon")
   * @param {string} equipmentString - Generic choice string
   * @param {object} character - Character data
   * @returns {object|null} Resolved item or null
   */
  static resolveGenericChoice(equipmentString, character) {
    const lower = equipmentString.toLowerCase();

    // Extract filters
    const isMartial = lower.includes('martial');
    const isSimple = lower.includes('simple');
    const isMelee = lower.includes('melee');
    const isRanged = lower.includes('ranged');
    const isWeapon = lower.includes('weapon');

    if (isWeapon) {
      // Simple weapons list (from D&D 5e SRD)
      const simpleWeapons = [
        'club', 'dagger', 'greatclub', 'handaxe', 'javelin', 'light_hammer',
        'mace', 'quarterstaff', 'sickle', 'spear', 'crossbow_light', 'dart',
        'shortbow', 'sling'
      ];

      // Get all weapons from BASE_ITEMS
      // [FIX NOV 24, 2025]: Include proper display name for each weapon
      const allWeapons = Object.entries(BASE_ITEMS.weapons || {})
        .map(([key, data]) => ({
          baseType: key,
          ...data,
          category: 'weapons',
          name: this.formatDisplayName(key)
        }))
        .filter(w => !w.magicalBonus); // Exclude magical weapons

      // Filter by simple/martial
      let filtered = allWeapons;
      if (isSimple) {
        filtered = filtered.filter(w => simpleWeapons.includes(w.baseType));
      } else if (isMartial) {
        filtered = filtered.filter(w => !simpleWeapons.includes(w.baseType));
      }

      // Filter by melee/ranged
      if (isMelee) {
        filtered = filtered.filter(w => {
          const props = w.properties || [];
          return !props.some(p => p.toLowerCase().includes('ammunition'));
        });
      } else if (isRanged) {
        filtered = filtered.filter(w => {
          const props = w.properties || [];
          return props.some(p => p.toLowerCase().includes('ammunition')) || props.some(p => p.toLowerCase().includes('thrown'));
        });
      }

      // Return default (first item) or null
      if (filtered.length > 0) {
        // Prefer iconic weapons as defaults
        const defaults = {
          'any martial melee weapon': 'longsword',
          'any martial weapon': 'longsword',
          'any simple melee weapon': 'club',
          'any simple weapon': 'club',
          'any simple ranged weapon': 'shortbow',
          'any martial ranged weapon': 'longbow'
        };

        const preferredDefault = defaults[lower.trim()];
        if (preferredDefault) {
          const preferred = filtered.find(w => w.baseType === preferredDefault);
          if (preferred) {
            return {
              ...preferred,
              source: 'GENERIC_DEFAULT',
              originalString: equipmentString
            };
          }
        }

        // Fallback to first filtered item
        return {
          ...filtered[0],
          source: 'GENERIC_DEFAULT',
          originalString: equipmentString
        };
      }
    }

    // Other generic types (musical instruments, artisan tools, etc.)
    // [C.2 APR 2026]: This is a last-resort fallback ONLY fired when the caller
    // couldn't resolve the user's specific choice (CharacterCreationWizard
    // substitutes selectedBackgroundTools[idx] before reaching here — see
    // wizard's generateFinalCharacter). Keeping Lute as default so users who
    // never open the dropdown still get *something*; they can swap it in
    // inventory later.
    if (lower.includes('musical instrument')) {
      const lute = this.findInBaseItems('lute');
      if (lute) {
        return {
          ...lute,
          source: 'GENERIC_DEFAULT',
          originalString: equipmentString,
          note: 'Default instrument — pick a specific one from the Musical instrument dropdown to override.'
        };
      }
    }

    if (lower.includes('artisan')) {
      // Return metadata for UI to show picker
      // For now, default to smith_tools
      const smithTools = this.findInBaseItems('smith_tools');
      if (smithTools) {
        return {
          ...smithTools,
          source: 'GENERIC_DEFAULT',
          originalString: equipmentString,
          note: 'Default artisan tools - can be changed later'
        };
      }
    }

    console.warn(`[EquipmentResolver] Could not resolve generic choice: "${equipmentString}"`);
    return null;
  }

  /**
   * Parse currency in equipment string ("Belt pouch containing 15 gp")
   * @param {string} equipmentString - Equipment string with currency
   * @returns {object|null} Item with currency metadata or null
   */
  static parseCurrency(equipmentString) {
    const currencyMatch = equipmentString.match(/^(.+?)\s+containing\s+(\d+)\s+(cp|sp|ep|gp|pp)$/i);

    if (currencyMatch) {
      // eslint-disable-next-line no-unused-vars
      const [_fullMatch, containerName, amount, type] = currencyMatch;

      // Resolve container
      const containerItems = this.resolveString(containerName, {});

      if (containerItems && containerItems.length > 0) {
        return {
          ...containerItems[0],
          containsCurrency: {
            amount: parseInt(amount),
            type: type.toLowerCase()
          },
          source: 'CURRENCY_COMBO',
          originalString: equipmentString
        };
      }
    }

    return null;
  }
}

/**
 * Helper function to resolve array of equipment
 * @param {Array} equipmentList - Array of equipment strings or choice objects
 * @param {object} character - Character data
 * @returns {Array} Flat array of all resolved items
 */
export function resolveEquipmentList(equipmentList, character = {}) {
  const resolved = [];

  for (const equipment of equipmentList) {
    const items = EquipmentResolver.resolve(equipment, character);
    if (items && items.length > 0) {
      resolved.push(...items);
    }
  }

  return resolved;
}

/**
 * Export for direct use
 */
export default EquipmentResolver;
